import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Search, Shield, ShieldOff, Crown, UserCheck, Check, X, MessageSquare, Trash2, EyeOff, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Members() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/members").then(({ data }) => setItems(data));
  const loadPending = () => api.get("/admin/pending").then(({ data }) => setPending(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user?.role === "admin" || user?.role === "owner") loadPending();
  }, [user]);

  const filtered = items.filter((u) =>
    u.name?.toLowerCase().includes(q.toLowerCase()) ||
    u.email?.toLowerCase().includes(q.toLowerCase())
  );

  const act = async (fn, msg, e) => {
    e?.stopPropagation();
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed");
    }
  };

  const promote = (uid, e) => act(() => api.post(`/admin/promote/${uid}`), "Promoted to admin", e);
  const demote = (uid, e) => act(() => api.post(`/admin/demote/${uid}`), "Removed from admin", e);
  const makeOwner = (uid, e) => act(() => api.post(`/admin/set-owner/${uid}`), "Promoted to owner", e);
  const removeOwner = (uid, e) => act(() => api.post(`/admin/remove-owner/${uid}`), "Owner role removed", e);
  const setActive = (uid, active, e) => act(() => api.post(`/admin/set-member/${uid}?active=${active}`), active ? "Access granted" : "Access revoked", e);
  const setCategory = (uid, cat, e) => act(() => api.post(`/admin/set-category/${uid}${cat ? `?category=${encodeURIComponent(cat)}` : ""}`), "Category updated", e);
  const setReadOnly = (uid, ro, e) => act(() => api.post(`/admin/set-readonly/${uid}?read_only=${ro}`), ro ? "Set to read-only" : "Read-only removed", e);

  const claimOwner = async () => {
    try {
      await api.post("/admin/claim-owner");
      toast.success("You are now the Owner 👑");
      await checkAuth();
      await load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed");
    }
  };

  const approve = (uid, e) => act(async () => { await api.post(`/admin/approve/${uid}`); await loadPending(); }, "Member approved", e);
  const reject = (uid, e) => act(async () => { await api.post(`/admin/reject/${uid}`); await loadPending(); }, "Member rejected", e);

  const removeMember = (uid, name, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Remove ${name} from CFAA? This permanently deletes their account and messages. This cannot be undone.`)) return;
    act(() => api.delete(`/admin/members/${uid}`), `${name} removed`, e);
  };

  const isOwner = user?.role === "owner";
  const isAdmin = user?.role === "admin" || isOwner;
  const ownerExists = items.some((u) => u.role === "owner");

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-purple-700 font-bold">Community</p>
        <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Members</h1>
        <p className="mt-2 text-slate-500">{items.length} people in the group</p>
      </div>

      {!ownerExists && (
        <div data-testid="claim-owner-banner" className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-white p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <p className="font-black text-slate-900">Claim the Owner role</p>
              <p className="text-sm text-slate-500">No owner has been set yet. Claim it to manage owners and admins.</p>
            </div>
          </div>
          <button
            data-testid="claim-owner-btn"
            onClick={claimOwner}
            className="inline-flex items-center justify-center gap-1 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold btn-lift"
          >
            <Crown className="w-4 h-4" /> Claim Owner
          </button>
        </div>
      )}

      {isAdmin && pending.length > 0 && (
        <div data-testid="pending-approvals" className="rounded-2xl border border-purple-200 bg-purple-50/50 p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserCheck className="w-5 h-5 text-purple-700" />
            <h2 className="font-black text-slate-900">Pending approvals ({pending.length})</h2>
          </div>
          <p className="text-sm text-slate-500 -mt-2 mb-4">Choose who comes in. Approve to grant access, or reject to deny.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pending.map((p) => (
              <div key={p.user_id} data-testid={`pending-card-${p.user_id}`} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100">
                <Avatar className="w-10 h-10 shrink-0">
                  <AvatarImage src={p.picture} />
                  <AvatarFallback className="bg-purple-700 text-white font-bold">{p.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900 truncate text-sm">{p.name}</p>
                  <p className="text-xs text-slate-500 truncate">{p.email}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={(e) => approve(p.user_id, e)} data-testid={`approve-${p.user_id}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 hover:bg-green-200 text-green-700" title="Approve">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={(e) => reject(p.user_id, e)} data-testid={`reject-${p.user_id}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 text-red-600" title="Reject">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="members-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search members…"
          className="pl-10 h-11 rounded-2xl border-slate-200"
        />
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((u) => {
          const role = u.role;
          const memberIsOwner = role === "owner";
          const memberIsAdmin = role === "admin";
          const isSelf = u.user_id === user?.user_id;
          return (
            <div key={u.user_id} data-testid={`member-card-${u.user_id}`}
                 className={`relative bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md btn-lift text-center ${memberIsOwner ? "border-amber-300" : memberIsAdmin ? "border-purple-200" : "border-slate-100"}`}>
              {memberIsOwner && (
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Owner
                </div>
              )}
              {memberIsAdmin && (
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Admin
                </div>
              )}
              <Avatar className={`w-16 h-16 mx-auto ring-4 ${memberIsOwner ? "ring-amber-300" : memberIsAdmin ? "ring-purple-200" : "ring-amber-300"}`}>
                <AvatarImage src={u.picture} />
                <AvatarFallback className={`${memberIsOwner ? "bg-amber-500" : "bg-purple-700"} text-white font-bold text-xl`}>
                  {u.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <h4 className="mt-3 font-bold text-slate-900 truncate">{u.name}</h4>
              <p className="text-xs text-slate-500 truncate">{u.email}</p>

              {!isSelf && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/app/messages?to=${u.user_id}`); }}
                  data-testid={`message-${u.user_id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-purple-700 hover:bg-purple-800 text-white text-xs font-semibold btn-lift"
                >
                  <MessageSquare className="w-3 h-3" /> Message
                </button>
              )}

              {isAdmin && !isSelf && (
                <div className="mt-3 space-y-2">
                  {/* Owner-only controls */}
                  {isOwner && memberIsOwner && (
                    <button
                      onClick={(e) => removeOwner(u.user_id, e)}
                      data-testid={`remove-owner-${u.user_id}`}
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold"
                    >
                      <Crown className="w-3 h-3" /> Remove owner
                    </button>
                  )}
                  {isOwner && !memberIsOwner && (
                    <button
                      onClick={(e) => makeOwner(u.user_id, e)}
                      data-testid={`make-owner-${u.user_id}`}
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs font-bold"
                    >
                      <Crown className="w-3 h-3" /> Make owner
                    </button>
                  )}
                  {/* Admin controls (hidden for owners unless current user is owner) */}
                  {!memberIsOwner && (
                    memberIsAdmin ? (
                      <button
                        onClick={(e) => demote(u.user_id, e)}
                        data-testid={`demote-${u.user_id}`}
                        className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold"
                      >
                        <ShieldOff className="w-3 h-3" /> Remove admin
                      </button>
                    ) : (
                      <button
                        onClick={(e) => promote(u.user_id, e)}
                        data-testid={`promote-${u.user_id}`}
                        className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs font-semibold"
                      >
                        <Shield className="w-3 h-3" /> Make admin
                      </button>
                    )
                  )}
                  {/* Access & membership category (owner/admin) */}
                  {!memberIsOwner && (
                    <div className="pt-2 mt-1 border-t border-slate-100 space-y-1.5">
                      <button
                        onClick={(e) => setActive(u.user_id, !u.is_active_member, e)}
                        data-testid={`toggle-active-${u.user_id}`}
                        className={`w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${u.is_active_member ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600"}`}
                      >
                        {u.is_active_member ? "✓ Active member" : "Grant chat access"}
                      </button>
                      <div className="flex gap-1">
                        {[["AR-15", "AR-15"], ["friend", "Friend"], [null, "None"]].map(([val, lbl]) => (
                          <button
                            key={lbl}
                            onClick={(e) => setCategory(u.user_id, val, e)}
                            data-testid={`category-${lbl.toLowerCase()}-${u.user_id}`}
                            className={`flex-1 px-1.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide ${((u.category || null) === val) ? "bg-purple-700 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-600"}`}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={(e) => setReadOnly(u.user_id, !u.read_only_manual, e)}
                        data-testid={`toggle-readonly-${u.user_id}`}
                        title="Read-only members can view everything and RSVP, but cannot comment, post or vote."
                        className={`w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${u.read_only_manual ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-600"}`}
                      >
                        {u.read_only_manual ? <><Eye className="w-3 h-3" /> Read-only ON</> : <><EyeOff className="w-3 h-3" /> Set read-only</>}
                      </button>
                      {u.read_only && !u.read_only_manual && (
                        <p className="text-[10px] text-amber-600 font-semibold text-center">Auto read-only (dues lapsed)</p>
                      )}
                    </div>
                  )}
                  {/* Remove member (delete account) */}
                  {(!memberIsOwner || isOwner) && (
                    <button
                      onClick={(e) => removeMember(u.user_id, u.name, e)}
                      data-testid={`remove-member-${u.user_id}`}
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold"
                    >
                      <Trash2 className="w-3 h-3" /> Remove member
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
